import { Skeleton } from 'antd'
import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

import {
  BENCHMARK_DELTA_COLOR,
  benchmarkDeltaTone,
  benchmarkDeltaTooltip,
  footerStatSlots,
  formatBenchmarkStat,
} from 'src/components/agent-forms/benchmark-stat-format'
import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'
import type { BenchmarkingGroupStats } from 'src/components/agent-forms/use-benchmark-compare-stats'

export interface GroupSummaryRowProps {
  entityType: BenchmarkingEntityType | null
  stats: BenchmarkingGroupStats | null
  /** Present ⇒ each `compared: true` slot gets a green/red/grey delta dot
   * against these numbers. Only the Focus card's row passes this (the
   * Universe stats), reproducing the old FOCUS/GROUP table's delta dots. */
  comparedTo?: BenchmarkingGroupStats | null
  isLoading: boolean
  isUnavailable: boolean
  /** Server-derived label for the trailing analysis window (e.g. "last 91
   * days") — see `BenchmarkingCompareStatsResponse.date_window_label`. */
  dateWindowLabel?: string | null
}

/**
 * The one-line stats summary at the bottom of each card in
 * `BenchmarkUniversePanel` — replaces the old 6-row FOCUS/GROUP table.
 * Renders three segments: Records, the selected entity's count
 * (Brands/Products, per `entityType`), and Avg sentiment. The entity-count
 * segment singularizes its label ("1 Brand") when the slot's own value is
 * exactly 1, via `BenchmarkStatSlot.singularLabel`. Segments render
 * unconditionally the moment the row mounts (only the numeric value
 * skeletons while a request is in flight), so the layout never disappears
 * and reappears on a refetch. Optionally renders the trailing analysis
 * window caption (e.g. "last 90 days") beneath the stat lines when
 * `dateWindowLabel` is passed — both the Focus card and the Universe card
 * pass the same server-derived label so the window reads consistently on
 * both halves of the comparison.
 */
export const GroupSummaryRow: React.FC<GroupSummaryRowProps> = ({
  entityType,
  stats,
  comparedTo,
  isLoading,
  isUnavailable,
  dateWindowLabel,
}) => {
  if (isUnavailable) {
    return (
      <UnavailableNote>
        Comparison stats are unavailable right now.
      </UnavailableNote>
    )
  }

  const showSkeleton = isLoading || !stats

  return (
    <Row>
      {footerStatSlots(entityType).map((slot) => {
        const tone =
          slot.compared && stats && comparedTo
            ? benchmarkDeltaTone(stats[slot.key], comparedTo[slot.key])
            : null
        const valueText =
          !showSkeleton && stats ? formatBenchmarkStat(slot, stats) : null
        const comparedText =
          tone && comparedTo ? formatBenchmarkStat(slot, comparedTo) : ''
        const label =
          slot.singularLabel && stats?.[slot.key] === 1
            ? slot.singularLabel
            : slot.label

        return (
          <Segment key={slot.key}>
            {tone && (
              <Tooltip content={benchmarkDeltaTooltip(tone, comparedText)}>
                <Dot
                  $color={BENCHMARK_DELTA_COLOR[tone]}
                  aria-label={benchmarkDeltaTooltip(tone, comparedText)}
                />
              </Tooltip>
            )}
            {valueText === null ? (
              <Skeleton.Button active size="small" style={SKELETON_STYLE} />
            ) : (
              <Value>{valueText}</Value>
            )}
            <Label>{label}</Label>
          </Segment>
        )
      })}
      {dateWindowLabel && <DateWindow>{dateWindowLabel}</DateWindow>}
    </Row>
  )
}

const SKELETON_STYLE = { width: 40, height: 16 }

const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`

const Segment = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]}px;
  white-space: nowrap;
`

const Value = styled.span`
  font-family: ${typography.fontFamily};
  font-weight: ${typography.weights.semibold};
  font-size: ${typography.text.xs.fontSize}px;
  color: ${gray[700]};
`

const Label = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.xs.fontSize}px;
  color: ${gray[500]};
`

const Dot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const UnavailableNote = styled.div`
  font-size: 12px;
  color: ${gray[500]};
  text-align: right;
`

const DateWindow = styled.span`
  font-size: 11px;
  color: ${gray[500]};
`
